import { Box, Center, Heading, Spinner, Text, VStack, useDisclosure } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { doHandleState } from '../../../redux/onlineSales/onlineSales.actions';
import OnlineSalesService from '../../../redux/onlineSales/onlineSales.services';
import { AvailableRewardsList } from '../components/points/AvailableRewardsList';
import { FamilyMembersList } from '../components/points/FamilyMembersList';
import { HistoryTabs } from '../components/points/HistoryTabs';
import { MemberDetailsModal } from '../components/points/MemberDetailsModal';
import { PointsSummaryCard } from '../components/points/PointsSummaryCard';
import { UserRewardsList } from '../components/points/UserRewardsList';

export default function Points() {
    const dispatch = useDispatch();
    const { loyaltyTiers, rewardsData, pointHistory, redeemHistory, loadingPoints } = useSelector((state) => state.onlineSales);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedMember, setSelectedMember] = useState(null);

    const fetchDashboardData = async () => {
        dispatch(doHandleState('loadingPoints', true));
        await dispatch(OnlineSalesService.doHandleGetLoyaltyTier());
        await dispatch(OnlineSalesService.doHandleGetAvailableRewards());
        await dispatch(OnlineSalesService.doHandleGetPointHistory());
        await dispatch(OnlineSalesService.doHandleGetRedeemHistory());
        dispatch(doHandleState('loadingPoints', false));
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const handleMemberClick = (member) => {
        setSelectedMember(member);
        onOpen();
    };

    // Mapping Data
    const totalPoints = rewardsData?.availableRewards?.currentPoints ? parseInt(rewardsData.availableRewards.currentPoints) : 0;

    const tier = useMemo(() => {
        const sortedTiers = [...(loyaltyTiers || [])].sort((a, b) => parseInt(a.MinPoints) - parseInt(b.MinPoints));

        let currentTierIndex = -1;
        for (let i = sortedTiers.length - 1; i >= 0; i--) {
            if (parseInt(sortedTiers[i].MinPoints) <= totalPoints) {
                currentTierIndex = i;
                break;
            }
        }

        const currentTierData = currentTierIndex >= 0 ? sortedTiers[currentTierIndex] : { TierName: 'BASIC' };
        const nextTierData = currentTierIndex >= 0 && currentTierIndex < sortedTiers.length - 1 ? sortedTiers[currentTierIndex + 1] : { ...currentTierData, MinPoints: parseInt(currentTierData.MinPoints || '0') };

        // To avoid infinity progress bar if max tier is reached
        const nextTierPoints = parseInt(nextTierData.MinPoints || '0');
        const finalNextTierPoints = nextTierPoints <= totalPoints ? totalPoints : nextTierPoints;

        return {
            name: currentTierData.TierName || 'BASIC',
            nextTierName: nextTierData.TierName || 'BASIC',
            nextTierPoints: finalNextTierPoints,
        };
    }, [loyaltyTiers, totalPoints]);

    const availableVouchers = useMemo(() => {
        return (
            rewardsData?.availableRewards?.availableRewards?.map((r) => ({
                id: r.RewardID,
                title: r.Title,
                description: r.Description,
                pointsRequired: parseInt(r.PointsRequired),
                stock: parseInt(r.Stock),
                isAffordable: r.IsAffordable === '1',
                image: '🎁', // placeholder for voucher image/icon
            })) || []
        );
    }, [rewardsData]);

    const userRewards = useMemo(() => {
        return (
            rewardsData?.userRewards
                ?.filter((r) => r.CurrentStatus === 'AVAILABLE')
                ?.map((r) => ({
                    id: `user-reward-${r.UserRewardID}`,
                    originalId: r.UserRewardID,
                    title: r.Title,
                    description: r.Description,
                    pointsRequired: parseInt(r.PointsRequired || '0'),
                    redeemCode: r.RedeemCode,
                    expiryDate: r.ExpiryDate,
                    currentStatus: r.CurrentStatus,
                    image: '🎟️',
                })) || []
        );
    }, [rewardsData]);

    const mappedPointHistory = useMemo(() => {
        return (pointHistory || []).map((p) => ({
            id: p.id,
            date: p.date,
            description: p.description,
            amount: parseInt(p.amount.replace('+', '')),
            status: p.type,
        }));
    }, [pointHistory]);

    const mappedRedeemHistory = useMemo(() => {
        return (redeemHistory || []).map((r, i) => ({
            id: r.code || i.toString(),
            date: r.timestamp,
            voucherName: r.title,
            code: r.code,
            pointsCost: Math.abs(parseInt(r.points)),
            status: r.status,
        }));
    }, [redeemHistory]);

    if (loadingPoints && !rewardsData?.availableRewards) {
        return (
            <Center h='50vh'>
                <Spinner size='xl' color='red.500' />
            </Center>
        );
    }

    return (
        <VStack spacing={{ base: 4, md: 6 }} align='stretch' w='full' pb={8}>
            <Box mt={{ base: 5, md: 0 }} mb={3}>
                <Heading size={{ base: 'lg', md: 'xl' }} color='gray.800' mb={{ base: 1, md: 2 }} textAlign={{ base: 'center', md: 'left' }} fontWeight='black'>
                    My Points
                </Heading>
                <Text color='gray.500' fontSize={{ base: 'sm', md: 'md' }} textAlign={{ base: 'center', md: 'left' }}>
                    Manage your family's points and redeem exciting rewards!
                </Text>
            </Box>

            {/* Total Points & Tier Summary */}
            <PointsSummaryCard totalPoints={totalPoints} tier={tier} />

            {/* User Rewards */}
            <UserRewardsList rewards={userRewards} />

            {/* Available Rewards */}
            <AvailableRewardsList rewards={availableVouchers} totalPoints={totalPoints} />

            {/* Family Members List */}
            <FamilyMembersList members={[]} onMemberClick={handleMemberClick} />

            {/* History Tabs (Points & Redeem) */}
            <HistoryTabs pointsHistory={mappedPointHistory} redeemHistory={mappedRedeemHistory} />

            {/* Member Details & Vouchers Modal */}
            <MemberDetailsModal isOpen={isOpen} onClose={onClose} member={selectedMember} availableVouchers={availableVouchers} />
        </VStack>
    );
}
